/**
 * DASHBOARD API CALLS
 *
 * The API calls for dashboard data and the required processing.
 *
 * @version 0.1
 */
import { findKey, upperFirst } from 'lodash';
import { timeFormat } from 'd3';

import { findIndex, forEach } from 'lodash';

import xhr from './../../../utils/xhr';
import utils from './../../../utils/utilities';

import { PESTLE_ORDER, GRAPHS_MIN_BAR_PERCENTAGE } from './../../../constants';

const api = {

    getTrendingEntitiesPestleScores: (filter) => {
        let companies = [];

        return xhr
            .post({
                url: '/api/aggregator/riskEntitiesCounts',
                data: filter
            })
            .then((response) => {
                companies = response.body;
                return companies;
            });
    },

    getElasticTrendingEntitiesPestleScores: (filter) => {
        let companies = [];

        return xhr
            .post({
                url: '/api/elastic/aggregator/riskEntitiesCounts',
                data: filter
            })
            .then((response) => {
                let companyIds = [];

                companies = response.body;

                for (let i = 0; i < companies.length; i++) {
                    companyIds.push(companies[i]._id);
                }

                return xhr.post({
                    url: '/api/entities/specific-companies',
                    data: companyIds
                });

            }).then((response) => {

                let companiesData = response.body;

                // Not all IDs match a company in the database (it might be
                // deleted), thus the array entry should be deleted as well
                for (let i = 0; i < companies.length; i++) {
                    let companyIndex;
                    companyIndex = findIndex(companiesData, {id: companies[i]._id});

                    if (companyIndex > -1) {
                        companies[i].title = companiesData[companyIndex].companyTitle;
                        companies[i].hasSanctionsAndWatchLists = companiesData[companyIndex].hasSanctionsAndWatchLists;
                        companies[i].experianBusinessId = companiesData[companyIndex].experianBusinessId;
                        companies[i].uiExperianDocument = companiesData[companyIndex].uiExperianDocument;
                        companies[i].id = companies[i]._id;
                    } else {
                        companies.splice(i, 1);
                        i--;
                    }
                }

                return Promise.resolve(companies);
            });
    },



    getSanctionedEntities: (filter) => {
        let companies = [];

        return xhr
            .post({
                url: '/api/sanctions/customer-unpaginated',
                data: filter
            })
            .then((response) => {

                let companyIds = [];

                companies = response.body;

                for (let i = 0; i < companies.length; i++) {
                    companyIds.push(companies[i].id);
                }

                return xhr.post({
                    url: '/api/entities/specific-companies',
                    data: companyIds
                });

            }).then((response) => {

                let companiesData = response.body;

                // Not all IDs match a company in the database (it might be
                // deleted), thus the array entry should be deleted as well
                for (let i = 0; i < companies.length; i++) {
                    let companyIndex = findIndex(companiesData, {id: companies[i].id});

                    if (companyIndex > -1) {
                        companies[i].title = companiesData[companyIndex].companyTitle;
                    } else {
                        companies.splice(i, 1);
                        i--;
                    }
                }

                return Promise.resolve(companies);
            });

    },

    getEntitiesWithExperian: (filter) => {
        let companies = [];

        return xhr
            .post({
                url: '/api/experian/customer-unpaginated',
                data: filter
            })
            .then((response) => {

                let companyIds = [];

                companies = response.body;

                for (let i = 0; i < companies.length; i++) {
                    companyIds.push(companies[i].id);
                }

                return xhr.post({
                    url: '/api/entities/specific-companies',
                    data: companyIds
                });

            }).then((response) => {

                let companiesData = response.body;

                // Not all IDs match a company in the database (it might be
                // deleted), thus the array entry should be deleted as well
                for (let i = 0; i < companies.length; i++) {
                    let companyIndex = findIndex(companiesData, {id: companies[i].id});

                    if (companyIndex > -1) {
                        companies[i].title = companiesData[companyIndex].companyTitle;
                    } else {
                        companies.splice(i, 1);
                        i--;
                    }
                }

                return Promise.resolve(companies);
            });

    },


    getTrendingTerms: (maxItems, filter) => {
        return xhr
            .post({
                url: '/api/elastic/aggregator/englishIndexTerms/' + maxItems,
                data: filter
            })
            .then((response) => {
                return Promise.resolve(response.body);
            });
    },

    /**
     * Fetches and processes the PESTLE article risk volumes to be displayed in the chart.
     *
     * @return  {Promise.<ChartData|Error>}   Returns a promise that's resolved with
     *                                        the processed chart data or an error.
     */
    getAndParsePestleRiskData: (filters) => {
        return xhr
            .post({
                url: '/api/elastic/aggregator/pestles',
                data: filters
            })
            .then((response) => {

                let risks = response.body,
                    pestleOrder = PESTLE_ORDER,
                    legendLabels = pestleOrder.map((value) => upperFirst(value)),
                    originalChartData = [legendLabels],
                    chartData = [legendLabels],
                    dayRowIndexes= {},
                    rowTotals = [],
                    dateFormat = timeFormat('%b %d');

                // Sort risks array by date ascending
                risks.sort((risk1, risk2) => {
                    if (risk1.day == risk2.day) {
                        return 0;
                    } else if (risk1.day > risk2.day) {
                        return 1;
                    } else {
                        return -1;
                    }
                });

                // Add counts to the chart matrix and also add X axis labels
                let day = null;

                for (let i = 0; i < risks.length; i++) {
                    day = risks[i].day;
                    let columnIndex = pestleOrder.indexOf(risks[i].pestleCategory),
                        rowIndex = dayRowIndexes[day.toString()];

                    if (!rowIndex) {
                        rowIndex = chartData.length;
                        dayRowIndexes[day.toString()] = rowIndex;
                        chartData.push([]);
                        chartData[rowIndex][columnIndex] = risks[i].count;
                        rowTotals[rowIndex] = risks[i].count;
                    } else {
                        chartData[rowIndex][columnIndex] = risks[i].count;
                        rowTotals[rowIndex] += risks[i].count
                    }


                }

                // Transform counts to percentages
                for (let i = 1; i < chartData.length; i++) {
                    let percentageToSubtract = 0,
                        articlesCountToSubtract = 0;

                    // Before resizing the bars that are too small, preserve the original chart data in another matrix
                    originalChartData.push([]);

                    for (let j = 0; j < legendLabels.length; j++) {
                        if (!chartData[i][j]) {
                            originalChartData[i][j] = 0;
                            chartData[i][j] = 0;
                        } else {

                            // Store the original article counts for the bar adjustments calculations that come
                            originalChartData[i][j] = chartData[i][j];
                            chartData[i][j] = (chartData[i][j] / rowTotals[i]) * 100;

                            // Limit the smallest bars to 3%
                            if (chartData[i][j] < GRAPHS_MIN_BAR_PERCENTAGE) {
                                percentageToSubtract += GRAPHS_MIN_BAR_PERCENTAGE;
                                articlesCountToSubtract += originalChartData[i][j];
                                chartData[i][j] = GRAPHS_MIN_BAR_PERCENTAGE;
                            }
                        }
                    }

                    // The percentage and total articles count that remain left for the larger bars, after growing the
                    // smallest ones. The largest bars will have their height recalculated using this remaining
                    // percentage. The formula is:
                    //     item article count / remaining total article count * remaining percentage
                    let remainingPercentage = 100 - percentageToSubtract;
                    let remainingArticlesCount = rowTotals[i] - articlesCountToSubtract;

                    // Simulate a resize on the larger bars and scale those who would have
                    // become less than the minimum accepted percentage, to this limit
                    for (let j = 0; j < legendLabels.length; j++) {
                        if (chartData[i][j] > GRAPHS_MIN_BAR_PERCENTAGE) {
                            let percentage = (originalChartData[i][j] / remainingArticlesCount) * remainingPercentage;

                            if (percentage < GRAPHS_MIN_BAR_PERCENTAGE) {
                                remainingPercentage -= GRAPHS_MIN_BAR_PERCENTAGE;
                                remainingArticlesCount -= originalChartData[i][j];
                                chartData[i][j] = GRAPHS_MIN_BAR_PERCENTAGE;
                            }
                        }
                    }

                    // Resize the rest of the remaining large bars
                    for (let j = 0; j < legendLabels.length; j++) {
                        if (chartData[i][j] > GRAPHS_MIN_BAR_PERCENTAGE) {
                            chartData[i][j] = (originalChartData[i][j] / remainingArticlesCount) * remainingPercentage;
                        }

                        // Calculate the real percentages after all other calculations are done
                        originalChartData[i][j] = (originalChartData[i][j] / rowTotals[i]) * 100;
                    }
                }

                // Create an array with the formatted dates
                let formattedDates = [];
                forEach(dayRowIndexes, (dayIndex, day) => {formattedDates[dayIndex - 1] = Number(day)});

                // Return the data in a chart-friendly format
                return {
                    days: formattedDates,
                    rowTotals: rowTotals,
                    originalChartData: originalChartData,

                    config: {
                        data: {
                            rows:   chartData,
                            groups: [legendLabels]
                        },
                        axis: {
                            x: {
                                tick: {
                                    format: (dayIndex)=> {
                                        let day = Number(findKey(dayRowIndexes, (index) => index === dayIndex + 1)),
                                            star = day === formattedDates[formattedDates.length - 1]? ' *' : '',
                                            formattedDate = dateFormat(
                                                utils.removeUTCOffset(utils.convertDaysToMilliseconds(day))
                                            );

                                        return (formattedDate ? formattedDate : 'N/A') + star;
                                    }
                                }
                            }
                        }
                    }
                };

            }).catch((error) => Promise.reject(error));
    },

    getPestleRiskData(filters) {
        return xhr
            .post({
                url: '/api/elastic/aggregator/pestles',
                data: filters
            })
    },

    getRiskEntityPESTLE: (riskEntityId, days, maxNo, pestle, risk) => {
        return xhr.post({
                    url: '/api/aggregator/riskEntityPestle/' + riskEntityId,
                    data: {
                        level: risk,
                        pestle: pestle,
                        noOfDays: days
                    }
                })
            .then((response) => {
                let data = response.body;
                data.sort(function compare(a, b) {
                    return b.totalCount - a.totalCount;
                });
                return Promise.resolve(data);
            });
    }
};

export default api;


// WEBPACK FOOTER //
// ./src/scripts/pages/StartPage/Dashboard/Dashboard.api.js